/** Maps stored GeneratedDocument.Type (ReportTypes name and/or query name) to German UI labels. */

const REPORT_TYPE_LABELS: Record<string, string> = {
  Order: 'Bestellung',
  ProductionOrder: 'Produktionsauftrag',
  DeliveryNote: 'Lieferschein',
  Export: 'Tabellenexport',
  BatchOpenOrdersDocument: 'Sammeldokument offene Bestellungen',
  Label: 'Etikett',
  UserIdentityCard: 'Mitarbeiterausweis',
  OwnProduction: 'Eigenproduktion',
  StockTransfer: 'Umlagerung',
  PackingList: 'Packliste',
  OutletStore: 'Outlet-Filiale',
  SaleReport: 'Umsatzbericht',
  WhoPacked: 'Wer hat gepackt',
  RoundLabelWithProductName: 'Rundetikett mit Produktname',
  Recipe: 'Rezept',
  Voucher: 'Gutschein',
  EanLabel: 'EAN-Etikett',
  RectangleLabel50x25: 'Etikett 50×25',
  RoundLabel58x58: 'Rundetikett 58×58',
  AttendeesList: 'Teilnehmerliste',
  RoundLabel50x50: 'Rundetikett 50×50',
  Email: 'E-Mail',
  Poster: 'Plakat',
  VoucherPercentage: 'Prozentgutschein',
  RedList: 'Red List',
  VoucherStore: 'Filialgutschein',
};

const QUERY_LABELS: Record<string, string> = {
  GetAllProductMaster: 'Artikelstammdaten',
  GetAllArticles: 'Artikelliste',
  GetAllArticleVariants: 'Variantenliste',
  GetAllBulkContainerArticles: 'Artikelliste',
  GetAllNonPackableArticles: 'Fremdmarke-Liste',
  GetAllBulkContainers: 'Großgebinde-Liste',
  GetAllOrders: 'Bestellliste Großgebinde',
  GetAllChangeLogs: 'Änderungsprotokoll',
  GetAllOwnProductions: 'Eigenproduktionsliste',
  GetOwnProductionByArticleId: 'Eigenproduktionen-Liste',
  GetAllProductionPlanning: 'Liste neuer Artikelproduktionen',
  GetAllUsers: 'Mitarbeiterliste',
  GetAllNonProductLabels: 'Non-Produkt-Etiketten',
  GetAllStorageLocationNumbers: 'Lagerplätze',
  GetAllRedLists: 'Red List',
  GetAllProductionOrders: 'Produktionsaufträge-Liste',
  GetAllBulkContainersOverview: 'Controlling Großgebinde-Liste',
  GetAllSuppliers: 'Lieferanten Liste',
  GetCartonConsumption: 'Kartonverbrauch',
  GetAllPostersPaged: 'Plakatliste',
  GetAllVouchersHistory: 'Gutscheinverlauf',
  GetAllStores: 'Filialen-Liste',
  GetAllSeminarEvents: 'Veranstaltungen',
  GetAllGeneratedDocuments: 'Exportliste',
  GetAllFlourArticleDetails: 'Flour-Review-Liste',
  GetMainStoreInactiveVariantsWithStock: 'Inaktive Varianten mit Bestand',
  GetAllSeminars: 'Seminare',
  GetAllArticleComments: 'Kommentare-Liste',
  GetAllPackingLists: 'Lieferscheinliste',
  GetAllRestockLists: 'Flour-Nachfüllliste',
  GetAllStockTransfers: 'Umlagerungsliste',
  GetAllUrlShortenerLinks: 'Linkkürzer-Liste',
  GetAllSeminarEventAttendees: 'Teilnehmer',
  GetAllRecipeCards: 'Rezeptkartenliste',
  GetAllLabelArticles: 'Variantenliste',
  GetAllArticleLabelsByArticleId: 'Artikel-Etiketten',
  GetAllEans: 'EAN-Liste',
  GetAllVariantsWithDuplicatedEan: 'Liste doppelter EAN',
  GetAllGiftboxArticles: 'Geschenkbox-Produktionsliste',
  GetAllComplaints: 'Reklamationen Liste',
  GetProductionControlling: 'Controlling Produktionsliste',
  GetControllingBulkContainers: 'Controlling Großgebinde-Liste',
  GetStoreStocks: 'Filialbestände-Liste',
  GetVouchers: 'Gutscheine',
  GetSeries: 'Gutscheinserien',
  GetAllCartonTypes: 'Kartons',
  GetAllInventoryItems: 'Bestandseinträge Liste',
  GetAllArticleVariantsWithStock: 'Controlling Bestandsliste',
  GetAllSeminarCategories: 'Kategorien',
  GetAllStorePhotoEntries: 'Filialfotos',
  GetAllBulkContainerCorrections: 'Inventurkorrekturen-Liste',
  GetBatchOpenOrdersDocuments: 'Chargenliste',
};

const TECHNICAL_NAME_RE = /^(Get[A-Z]|[A-Z][A-Za-z]+\/)/;

function typeParts(type: string | null | undefined): { reportType: string; queryName: string } {
  const raw = (type || '').trim();
  const slash = raw.indexOf('/');
  if (slash === -1) {
    return { reportType: raw, queryName: raw };
  }
  return {
    reportType: raw.slice(0, slash),
    queryName: raw.slice(slash + 1),
  };
}

export function getExportTypeLabel(type: string | null | undefined): string {
  const { reportType, queryName } = typeParts(type);
  if (queryName && QUERY_LABELS[queryName]) {
    return QUERY_LABELS[queryName];
  }
  if (reportType && REPORT_TYPE_LABELS[reportType]) {
    return REPORT_TYPE_LABELS[reportType];
  }
  if (queryName && REPORT_TYPE_LABELS[queryName]) {
    return REPORT_TYPE_LABELS[queryName];
  }
  return 'Dokument';
}

export function isTechnicalExportName(visibleName: string | null | undefined, type: string | null | undefined): boolean {
  const name = (visibleName || '').trim();
  if (!name) {
    return true;
  }
  const { queryName, reportType } = typeParts(type);
  if (name === type || name === queryName || name === reportType) {
    return true;
  }
  if (QUERY_LABELS[name] || REPORT_TYPE_LABELS[name]) {
    return true;
  }
  return TECHNICAL_NAME_RE.test(name) || name.includes('/');
}

export function getExportDocumentName(
  doc: { visibleName?: string; type?: string; createdAt?: string },
  formatDate: (value: string) => string | null,
): string {
  const typeLabel = getExportTypeLabel(doc?.type);
  const visible = (doc?.visibleName || '').trim();
  if (visible && !isTechnicalExportName(visible, doc?.type)) {
    return visible;
  }
  const dateStr = doc?.createdAt ? formatDate(doc.createdAt) : null;
  if (typeLabel && dateStr) {
    return `${typeLabel} – ${dateStr}`;
  }
  return typeLabel || dateStr || '–';
}
